import { Alert, Button, Input, LoadingState, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useBootstrap } from '../../lib/catalog.ts'
import { useWorkItemDetail } from '../work-item/api.ts'
import { WorkItemDetailView } from '../work-item/WorkItemDetail.tsx'
import {
  type TemplateResumo,
  useAbrirSessaoTemplate,
  useCancelarSessaoTemplate,
  useSalvarSessaoTemplate,
} from './template-data.ts'
import './template-no-item.css'

/**
 * Editar template com A MESMA TELA do Item de Trabalho (SPEC-20260830-1530).
 *
 * Abrir materializa o template como Item de Trabalho de BASTIDOR e entrega o id
 * para o `WorkItemDetailView`; salvar captura a cadeia de volta para o
 * `template_payload` e descarta o bastidor; cancelar só descarta.
 *
 * Por isso não há nenhuma tela de template aqui — só a moldura (nome, descrição
 * e a barra de Salvar/Cancelar). Tudo que o Item de Trabalho ganhar aparece
 * aqui no mesmo dia, sem ninguém precisar lembrar.
 */
export function TemplateNoItemPage({
  template,
  onFechar,
}: {
  /** `null` = criar um template do zero. */
  template: TemplateResumo | null
  onFechar: () => void
}) {
  const abrir = useAbrirSessaoTemplate(template?.id ?? 'novo')
  const salvar = useSalvarSessaoTemplate()
  const cancelar = useCancelarSessaoTemplate()
  const bootstrap = useBootstrap()
  const toast = useToast()

  /** RAIZ do bastidor: é ela que é capturada ao salvar, esteja onde estiver. */
  const workItemId = abrir.data?.workItemId ?? null
  // template novo ainda não tem id: salvar e cancelar vão como `novo` (SPEC-20261026-2253-criar-template-erro-interno)
  const templateId = abrir.data?.templateId ?? template?.id ?? 'novo'
  const [nome, setNome] = useState(template?.name ?? '')
  const [descricao, setDescricao] = useState(template?.description ?? '')

  /**
   * Trilha de navegação DENTRO do template (pedido 2026-08-30): dá para entrar
   * em qualquer filho e configurá-lo — tipo, etapa, campos, checklists, netos —
   * para que a cadeia inteira nasça pronta ao aplicar. A raiz é sempre o
   * primeiro item da trilha; salvar captura a raiz, não o item aberto.
   */
  const [trilha, setTrilha] = useState<string[]>([])
  const itemAberto = trilha.length > 0 ? trilha[trilha.length - 1]! : workItemId
  const detalhe = useWorkItemDetail(itemAberto)
  const raiz = useWorkItemDetail(workItemId)

  /**
   * Cancelar descarta o bastidor. O `onFechar` só roda DEPOIS: fechar primeiro
   * desmontava o componente e a requisição de descarte era abortada em voo —
   * os bastidores ficavam acumulando no banco.
   */
  const fecharDescartando = () => {
    if (!workItemId || !templateId) {
      onFechar()
      return
    }
    cancelar.mutate({ templateId, workItemId }, { onSettled: onFechar })
  }

  if (abrir.isError) {
    return (
      <div className="tpli">
        <Alert tone="danger">Não foi possível abrir o template para edição: {(abrir.error as Error).message}</Alert>
        <Button variant="secondary" onClick={onFechar}>
          Voltar
        </Button>
      </div>
    )
  }
  if (!workItemId || detalhe.isLoading || !detalhe.data || !bootstrap.data) {
    return <LoadingState label="Abrindo o template…" />
  }

  const podeSalvar = nome.trim() !== '' && !salvar.isPending

  return (
    <div className="tpli">
      {/* moldura do template: só o que NÃO é do item */}
      <div className="tpli__barra">
        <i className="fas fa-clone tpli__ico" aria-hidden />
        <div className="tpli__campos">
          <label className="tpli__campo">
            <span>Nome do template</span>
            <Input value={nome} placeholder="Ex.: Onboarding de cliente" onChange={(e) => setNome(e.target.value)} />
          </label>
          <label className="tpli__campo tpli__campo--larga">
            <span>Descrição</span>
            <Input
              value={descricao}
              placeholder="Aparece na hora de escolher o template"
              onChange={(e) => setDescricao(e.target.value)}
            />
          </label>
        </div>
        <div className="tpli__acoes">
          <Button variant="secondary" onClick={fecharDescartando} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon="fas fa-check"
            disabled={!podeSalvar}
            loading={salvar.isPending}
            onClick={() => {
              if (!templateId) {
                toast.push({ tone: 'danger', message: 'Sessão de template inválida.' })
                return
              }
              salvar.mutate(
                { templateId, workItemId, name: nome.trim(), description: descricao.trim() },
                {
                  onSuccess: (r) => {
                    toast.push({
                      tone: 'success',
                      message: `Template salvo com ${r.itens} ${r.itens === 1 ? 'item' : 'itens'}.`,
                    })
                    onFechar()
                  },
                  onError: (e) => toast.push({ tone: 'danger', message: (e as Error).message }),
                },
              )
            }}
          >
            Salvar template
          </Button>
        </div>
      </div>

      <p className="tpli__aviso">
        Você está montando um <strong>modelo</strong>: o que estiver aqui é o que os itens vão ter ao aplicar. Não há
        movimentação, histórico nem comentários — e a etapa é sempre a inicial do fluxo escolhido.
      </p>

      {/* Trilha: aparece ao entrar num filho, para voltar de qualquer nível */}
      {trilha.length > 0 ? (
        <nav className="tpli__trilha" aria-label="Onde você está no template">
          <button type="button" className="tpli__trilha-item" onClick={() => setTrilha([])}>
            <i className="fas fa-home" aria-hidden /> {raiz.data?.item.title ?? 'Raiz do template'}
          </button>
          {trilha.map((id, i) => (
            <span key={id} className="tpli__trilha-parte">
              <i className="fas fa-chevron-right tpli__trilha-sep" aria-hidden />
              {i === trilha.length - 1 ? (
                <strong className="tpli__trilha-atual">{detalhe.data?.item.title ?? '…'}</strong>
              ) : (
                <button type="button" className="tpli__trilha-item" onClick={() => setTrilha(trilha.slice(0, i + 1))}>
                  …
                </button>
              )}
            </span>
          ))}
        </nav>
      ) : null}

      {/* A MESMA tela do Item de Trabalho, editando o item de bastidor */}
      <div className="tpli__item">
        <WorkItemDetailView
          key={itemAberto ?? 'raiz'}
          detail={detalhe.data}
          bootstrap={bootstrap.data}
          // entra no filho para configurá-lo (tipo, etapa, campos, netos)
          onOpenItem={(id) => setTrilha((t) => [...t, id])}
          // dentro de um filho, o X sobe um nível; na raiz, fecha o template
          onBack={() => (trilha.length > 0 ? setTrilha((t) => t.slice(0, -1)) : fecharDescartando())}
          canGoBack={trilha.length > 0}
          modoTemplate
        />
      </div>
    </div>
  )
}
